import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Alert
} from '@mui/material';
import { createRafResizeObserver } from '../../Utils/resizeObserver';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { VariableSizeList as VirtualList } from 'react-window';
import { useComponentWebSocket } from './GlobalSettingUtils';
import { useTranslation } from 'react-i18next';

const LOG_ROW_VERTICAL_PADDING = 16;
const LOG_ROW_HORIZONTAL_PADDING = 16;
const LOG_METADATA_WIDTH = 150;
const LOG_MESSAGE_LINE_HEIGHT = 18;
const LOG_MESSAGE_FONT_SIZE = '0.72rem';
const LOG_MESSAGE_MAX_HEIGHT = 320;
const SERVICE_STATUS_REFRESH_INTERVAL = 10000;
const HIDDEN_SERVICE_STATUS_KEYS = new Set(['webserver']);
const NON_CONTROLLABLE_SERVICE_STATUS_KEYS = new Set(['api']);
const LOG_TIMESTAMP_PATTERN = /^(?:\[([^\]]+)\]\s+)?(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})(?:[.,](\d{1,3}))?/;

const formatLogTimestamp = (timestamp) => {
  if (!timestamp) return '';

  const timestampText = String(timestamp).trim();
  const match = timestampText.match(LOG_TIMESTAMP_PATTERN);

  if (match) {
    const [, , year, month, day, hour, minute, second, milliseconds = '0'] = match;
    const localDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      Number(milliseconds.padEnd(3, '0'))
    );

    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toLocaleString();
    }
  }

  return timestampText;
};

const getLogLevel = (logLine) => {
  const lowerLine = logLine.toLowerCase();
  if (lowerLine.includes('error') || lowerLine.includes('exception') || lowerLine.includes('failed')) {
    return 'ERROR';
  }
  if (lowerLine.includes('warning') || lowerLine.includes('warn')) {
    return 'WARNING';
  }
  if (lowerLine.includes('debug')) {
    return 'DEBUG';
  }
  return 'INFO';
};

const LOG_LEVEL_PRIORITY = {
  DEBUG: 0,
  INFO: 1,
  WARNING: 2,
  ERROR: 3
};

const processLogLines = (logStrings, selectedLogFile) => {
  const timestampsBySource = new Map();
  const processedLogs = [];
  let currentLog = null;

  const flushCurrentLog = () => {
    if (!currentLog) return;

    const { sourceKey, ...log } = currentLog;
    processedLogs.push(log);
    currentLog = null;
  };

  logStrings.forEach((logLine, index) => {
    // A timestamp starts a new logical entry. Lines without one are continuation
    // lines from the preceding entry, such as Python traceback frames.
    const timestampMatch = logLine.match(
      /^(?:\[([^\]]+)\]\s+)?(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:[.,]\d{3})?)/
    );
    const sourceMatch = logLine.match(/^\[([^\]]+)\]\s+/);
    const sourceKey = timestampMatch?.[1] || sourceMatch?.[1] || selectedLogFile;
    const timestamp = timestampMatch?.[2] || timestampsBySource.get(sourceKey) || '';

    if (timestampMatch) {
      timestampsBySource.set(sourceKey, timestamp);
    }

    if (timestampMatch || !currentLog || currentLog.sourceKey !== sourceKey) {
      flushCurrentLog();
      currentLog = {
        id: index,
        timestamp,
        displayTimestamp: formatLogTimestamp(timestamp),
        level: getLogLevel(logLine),
        message: logLine,
        rawLine: logLine,
        sourceKey
      };
      return;
    }

    currentLog.message = `${currentLog.message}\n${logLine}`;
    currentLog.rawLine = `${currentLog.rawLine}\n${logLine}`;
    const continuationLevel = getLogLevel(logLine);
    if (LOG_LEVEL_PRIORITY[continuationLevel] > LOG_LEVEL_PRIORITY[currentLog.level]) {
      currentLog.level = continuationLevel;
    }
  });

  flushCurrentLog();
  return processedLogs;
};

// Memoized log item component for better performance
const LogItem = React.memo(({ index, style, data }) => {
  const { logs, getLevelColor } = data;
  const log = logs[index];
  
  if (!log) return null;

  return (
    <div style={style}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1,
          py: 1,
          borderBottom: '1px solid #f0f0f0',
          fontSize: '0.75rem',
          textAlign: 'left',
          minHeight: 'auto',
          boxSizing: 'border-box'
        }}
      >
        <Box
          sx={{
            width: LOG_METADATA_WIDTH,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: 'flex-start'
          }}
        >
          <Chip
            label={log.level}
            size="small"
            sx={{
              backgroundColor: getLevelColor(log.level),
              color: 'white',
              fontWeight: 'bold',
              minWidth: 58,
              maxWidth: 74,
              height: 20,
              fontSize: '0.65rem',
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.68rem',
              whiteSpace: 'nowrap',
              textAlign: 'left'
            }}
          >
            {log.displayTimestamp || '-'}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            pr: 0.5
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: LOG_MESSAGE_FONT_SIZE,
              lineHeight: `${LOG_MESSAGE_LINE_HEIGHT}px`,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              display: 'block',
              margin: 0,
              px: 1,
              py: 0.75,
              maxHeight: LOG_MESSAGE_MAX_HEIGHT,
              overflow: 'auto',
              backgroundColor: '#fafafa',
              border: '1px solid #eeeeee',
              borderRadius: 1
            }}
          >
            {log.message}
          </Typography>
        </Box>
      </Box>
    </div>
  );
});

LogItem.displayName = 'LogItem';

const GlobalSettingLogs = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [availableLogs, setAvailableLogs] = useState([]);
  const [selectedLogFile, setSelectedLogFile] = useState('all');
  const [serviceStatuses, setServiceStatuses] = useState([]);
  const [serviceStatusLoading, setServiceStatusLoading] = useState(false);
  const [serviceStatusError, setServiceStatusError] = useState('');
  const [serviceStatusLastRefresh, setServiceStatusLastRefresh] = useState(null);
  const [serviceActionLoading, setServiceActionLoading] = useState({});
  const [serviceActionFeedback, setServiceActionFeedback] = useState(null);
  const [postprocessingRetries, setPostprocessingRetries] = useState([]);
  const [postprocessingRetryTotal, setPostprocessingRetryTotal] = useState(0);
  const [postprocessingRetryState, setPostprocessingRetryState] = useState('all');
  const [postprocessingRetryLoading, setPostprocessingRetryLoading] = useState(false);
  const [postprocessingRetryError, setPostprocessingRetryError] = useState('');
  const [postprocessingRetryLastRefresh, setPostprocessingRetryLastRefresh] = useState(null);
  const [listWidth, setListWidth] = useState(900);
  const listRef = React.useRef(null);
  const listContainerRef = React.useRef(null);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // WebSocket command handlers
  const commandHandlers = {
    'get_logs_response': (message) => {
      console.log('📥 Received logs response:', message);
      
      // Extract data object - WebSocket sends {command: "...", data: {...}}
      const responseData = message.data || message;
      
      // Backend returns {status: "success", logs: [...], total_logs: N, available_logs: [...]} or {status: "error", message: "...", logs: []}
      if (responseData.status === 'error') {
        console.error('Error fetching logs:', responseData.message);
        setLogs([]);
        setLoading(false);
        return;
      }
      
      // Update available log files for dropdown
      if (responseData.available_logs && Array.isArray(responseData.available_logs)) {
        setAvailableLogs(responseData.available_logs);
      }
      
      // Backend now returns simple string array, convert to objects for frontend use
      const logStrings = responseData.logs || [];
      console.log('📊 Log strings received:', logStrings.length, 'lines');
      
      const processedLogs = processLogLines(logStrings, selectedLogFile);
      
      console.log('✅ Processed logs:', processedLogs.length, 'items');
      console.log('📋 First few logs:', processedLogs.slice(0, 3));
      
      setLogs(processedLogs);
      setLoading(false);
      setLastRefresh(new Date());
      
      // Scroll to bottom after logs are loaded
      setTimeout(() => {
        if (listRef.current && processedLogs.length > 0) {
          listRef.current.scrollToItem(processedLogs.length - 1, 'end');
        }
      }, 100);
    },
    'service_statuses': (message) => {
      const responseData = message.data || message;

      if (responseData.status === 'error' || !Array.isArray(responseData.services)) {
        setServiceStatuses([]);
        setServiceStatusError(
          responseData.message || t('global_setting.service_status_error', 'Unable to load service statuses.')
        );
        setServiceStatusLoading(false);
        return;
      }

      setServiceStatuses(
        responseData.services.filter((service) => !HIDDEN_SERVICE_STATUS_KEYS.has(service?.key))
      );
      setServiceStatusError('');
      setServiceStatusLoading(false);
      setServiceStatusLastRefresh(responseData.checked_at || new Date());
    },
    'postprocessing_retries': (message) => {
      const responseData = message.data || message;

      if (responseData.status === 'error' || !Array.isArray(responseData.retries)) {
        setPostprocessingRetries([]);
        setPostprocessingRetryTotal(0);
        setPostprocessingRetryError(
          responseData.message || t(
            'global_setting.postprocessing_retry_error',
            'Unable to load post-processing retries.'
          )
        );
        setPostprocessingRetryLoading(false);
        return;
      }

      setPostprocessingRetries(responseData.retries);
      setPostprocessingRetryTotal(Number(responseData.total) || responseData.retries.length);
      setPostprocessingRetryError('');
      setPostprocessingRetryLoading(false);
      setPostprocessingRetryLastRefresh(responseData.checked_at || new Date());
    },
    'service_control': (message) => {
      const responseData = message.data || message;
      const serviceKey = String(responseData.service_key || '').trim();
      if (serviceKey) {
        setServiceActionLoading((current) => {
          const next = { ...current };
          delete next[serviceKey];
          return next;
        });
      }

      if (responseData.status === 'error') {
        if (!serviceKey) {
          setServiceActionLoading({});
        }
        setServiceActionFeedback({
          severity: 'error',
          message: responseData.message || t('global_setting.service_action_error', 'Unable to control service.')
        });
        return;
      }

      setServiceActionFeedback({
        severity: 'success',
        message: responseData.status === 'already_running'
          ? t('global_setting.service_action_already_running', 'The service is already running.')
          : t('global_setting.service_action_accepted', 'Service action accepted.')
      });
      setTimeout(() => sendCommand('get_service_statuses'), 1000);
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    refreshData: () => {
      fetchLogs();
      fetchPostprocessingRetries();
    },
    isModified: () => false,
    saveChanges: () => Promise.resolve(true)
  }));  // Fetch logs from server
  const fetchLogs = useCallback(() => {
    setLoading(true);
    sendCommand('get_logs', { limit: 1000, log_file: selectedLogFile });
  }, [selectedLogFile, sendCommand]);

  const fetchServiceStatuses = useCallback(() => {
    setServiceStatusLoading(true);
    sendCommand('get_service_statuses');
  }, [sendCommand]);

  const fetchPostprocessingRetries = useCallback(() => {
    setPostprocessingRetryLoading(true);
    sendCommand('get_postprocessing_retries', {
      limit: 100,
      state: postprocessingRetryState === 'all' ? '' : postprocessingRetryState,
    });
  }, [postprocessingRetryState, sendCommand]);

  useEffect(() => {
    fetchServiceStatuses();
    const refreshTimer = setInterval(fetchServiceStatuses, SERVICE_STATUS_REFRESH_INTERVAL);
    return () => clearInterval(refreshTimer);
  }, [fetchServiceStatuses]);

  useEffect(() => {
    fetchPostprocessingRetries();
    const refreshTimer = setInterval(fetchPostprocessingRetries, SERVICE_STATUS_REFRESH_INTERVAL);
    return () => clearInterval(refreshTimer);
  }, [fetchPostprocessingRetries]);

  // Memoized filtered logs for better performance
  const filteredLogs = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    
    let filtered = logs;

    if (debouncedSearchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        log.level.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level.toLowerCase() === levelFilter);
    }

    return filtered;
  }, [logs, debouncedSearchTerm, levelFilter]);

  // Download logs as text file
  const downloadLogs = useCallback(() => {
    const logsToDownload = filteredLogs.length > 0 ? filteredLogs : logs;
    if (!Array.isArray(logsToDownload) || logsToDownload.length === 0) {
      return; // Don't download if no logs
    }
    
    const logText = logsToDownload.map(log => 
      `[${log.displayTimestamp || log.timestamp}] ${log.level.toUpperCase()}: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [logs, filteredLogs]);

  // Get log level color - memoized
  const getLevelColor = useCallback((level) => {
    switch (level.toLowerCase()) {
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      case 'info': return '#2196f3';
      case 'debug': return '#9e9e9e';
      default: return '#757575';
    }
  }, []);

  const getServiceStatusColor = useCallback((status) => {
    switch (String(status || '').toLowerCase()) {
      case 'running': return 'success';
      case 'stopped': return 'error';
      default: return 'warning';
    }
  }, []);

  const getServiceStatusLabel = useCallback((status) => (
    t(`global_setting.service_status_${String(status || 'unknown').toLowerCase()}`, 'Unknown')
  ), [t]);

  const getServiceLabel = useCallback((service) => (
    t(`global_setting.service_status_${service.key}`, service.name)
  ), [t]);

  const handleServiceAction = useCallback((service, action) => {
    if (action === 'restart' && typeof window !== 'undefined') {
      const confirmed = window.confirm(
        t('global_setting.service_restart_confirm', 'Restart {{service}}?', {
          service: getServiceLabel(service)
        })
      );
      if (!confirmed) return;
    }

    setServiceActionFeedback(null);
    setServiceActionLoading((current) => ({ ...current, [service.key]: action }));
    sendCommand('control_service', {
      service_key: service.key,
      action
    });
  }, [getServiceLabel, sendCommand, t]);
  // Memoized data for virtual list
  const virtualListData = useMemo(() => ({
    logs: filteredLogs,
    getLevelColor
  }), [filteredLogs, getLevelColor]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.resetAfterIndex(0, true);
  }, [filteredLogs, listWidth]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const resizeObserver = createRafResizeObserver(([entry]) => {
      const nextWidth = Math.max(320, Math.floor(entry.contentRect.width));
      setListWidth((previousWidth) => previousWidth === nextWidth ? previousWidth : nextWidth);
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate item height based on content
  const getItemSize = useCallback((index) => {
    const log = filteredLogs[index];
    if (!log) return 50;
    
    const messageWidth = Math.max(
      220,
      listWidth - LOG_METADATA_WIDTH - LOG_ROW_HORIZONTAL_PADDING - 16
    );
    const averageCharacterWidth = 7.2;
    const charactersPerLine = Math.max(24, Math.floor(messageWidth / averageCharacterWidth));
    const visualLines = log.message.split('\n').reduce((lineCount, segment) => {
      return lineCount + Math.max(1, Math.ceil(segment.length / charactersPerLine));
    }, 0);
    const textHeight = Math.min(
      LOG_MESSAGE_MAX_HEIGHT,
      visualLines * LOG_MESSAGE_LINE_HEIGHT + 14
    );
    
    return Math.max(58, textHeight + LOG_ROW_VERTICAL_PADDING + 2);
  }, [filteredLogs, listWidth]);
  // Auto-refresh functionality - DISABLED
  // useEffect(() => {
  //   if (autoRefresh) {
  //     autoRefreshInterval.current = setInterval(fetchLogs, 5000); // Refresh every 5 seconds
  //   } else {
  //     if (autoRefreshInterval.current) {
  //       clearInterval(autoRefreshInterval.current);
  //     }
  //   }

  //   return () => {
  //     if (autoRefreshInterval.current) {
  //       clearInterval(autoRefreshInterval.current);
  //     }
  //   };
  // }, [autoRefresh]);
  // Initial load
  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    if (timestamp instanceof Date) {
      return Number.isNaN(timestamp.getTime()) ? '-' : timestamp.toLocaleString();
    }
    const timestampText = String(timestamp);
    const normalizedTimestamp = /(?:Z|[+-]\d{2}:?\d{2})$/.test(timestampText)
      ? timestampText
      : `${timestampText}Z`;
    const date = new Date(normalizedTimestamp);
    return Number.isNaN(date.getTime()) ? timestampText : date.toLocaleString();
  };

  const getRetryStateColor = (state, isStale) => {
    if (isStale) return 'error';
    switch (String(state || '').toLowerCase()) {
      case 'scheduled': return 'info';
      case 'buffered': return 'warning';
      case 'processing': return 'primary';
      case 'finalizing': return 'secondary';
      default: return 'default';
    }
  };

  const getRetryStateLabel = (state, isStale) => {
    if (isStale) return t('global_setting.postprocessing_retry_stale', 'Stale');
    return t(
      `global_setting.postprocessing_retry_state_${String(state || 'unknown').toLowerCase()}`,
      state || 'Unknown'
    );
  };

  return (
    <Box sx={{ maxWidth: '100%', width: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#5757b0' }}>
        {t('global_setting.system_logs_title', 'System Logs')}
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('global_setting.system_logs_description', 'View and manage application logs. Use filters to find specific log entries.')}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5757b0', flex: 1 }}>
            {t('global_setting.service_status_title', 'Service Status')}
          </Typography>
          <Tooltip title={t('global_setting.service_status_refresh', 'Refresh service status')}>
            <span>
              <IconButton
                onClick={fetchServiceStatuses}
                disabled={serviceStatusLoading}
                color="primary"
                aria-label={t('global_setting.service_status_refresh', 'Refresh service status')}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {t('global_setting.service_status_description', 'Monitor the availability of the DataUnfold services.')}
        </Typography>

        {serviceStatusError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serviceStatusError}
          </Alert>
        )}

        {serviceActionFeedback && (
          <Alert severity={serviceActionFeedback.severity} sx={{ mb: 2 }}>
            {serviceActionFeedback.message}
          </Alert>
        )}

        {serviceStatusLoading && serviceStatuses.length === 0 ? (
          <Alert severity="info">
            {t('global_setting.service_status_loading', 'Checking service status...')}
          </Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 1.5 }}>
            {serviceStatuses.map((service) => (
              <Box
                key={service.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1.5,
                  border: '1px solid #eeeeee',
                  borderRadius: 1,
                  backgroundColor: '#fafafa'
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {getServiceLabel(service)}
                  </Typography>
                  {service.endpoint && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', overflowWrap: 'anywhere' }}
                    >
                      {service.endpoint}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size="small"
                  color={getServiceStatusColor(service.status)}
                  label={getServiceStatusLabel(service.status)}
                />
                {!NON_CONTROLLABLE_SERVICE_STATUS_KEYS.has(service.key) && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      service.status === 'unknown'
                      || serviceStatusLoading
                      || Boolean(serviceActionLoading[service.key])
                    }
                    onClick={() => handleServiceAction(
                      service,
                      service.status === 'running' ? 'restart' : 'start'
                    )}
                  >
                    {serviceActionLoading[service.key]
                      ? t('global_setting.service_action_in_progress', 'Working...')
                      : service.status === 'running'
                        ? t('global_setting.service_restart', 'Restart')
                        : t('global_setting.service_start', 'Start')}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}

        {serviceStatusLastRefresh && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {t('global_setting.service_status_last_updated', 'Last checked: {{time}}', {
              time: formatTimestamp(serviceStatusLastRefresh)
            })}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5757b0', flex: 1 }}>
            {t('global_setting.postprocessing_retry_title', 'Post-processing retries')}
          </Typography>
          <Tooltip title={t('global_setting.postprocessing_retry_refresh', 'Refresh retries')}>
            <span>
              <IconButton
                onClick={fetchPostprocessingRetries}
                disabled={postprocessingRetryLoading}
                color="primary"
                aria-label={t('global_setting.postprocessing_retry_refresh', 'Refresh retries')}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          {t(
            'global_setting.postprocessing_retry_description',
            'Inspect failed post-processing actions, where they occurred, and when they will run again.'
          )}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('global_setting.postprocessing_retry_state_filter', 'State')}</InputLabel>
            <Select
              value={postprocessingRetryState}
              label={t('global_setting.postprocessing_retry_state_filter', 'State')}
              onChange={(event) => setPostprocessingRetryState(event.target.value)}
            >
              <MenuItem value="all">
                {t('global_setting.postprocessing_retry_state_all', 'All states')}
              </MenuItem>
              {['scheduled', 'buffered', 'processing', 'finalizing'].map((state) => (
                <MenuItem key={state} value={state}>
                  {getRetryStateLabel(state, false)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip
            size="small"
            label={t('global_setting.postprocessing_retry_total', '{{count}} retries', {
              count: postprocessingRetryTotal
            })}
          />
          {postprocessingRetryLastRefresh && (
            <Typography variant="caption" color="text.secondary">
              {t('global_setting.postprocessing_retry_last_updated', 'Last checked: {{time}}', {
                time: formatTimestamp(postprocessingRetryLastRefresh)
              })}
            </Typography>
          )}
        </Box>

        {postprocessingRetryError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {postprocessingRetryError}
          </Alert>
        )}

        {postprocessingRetryLoading && postprocessingRetries.length === 0 ? (
          <Alert severity="info">
            {t('global_setting.postprocessing_retry_loading', 'Loading post-processing retries...')}
          </Alert>
        ) : postprocessingRetries.length === 0 ? (
          <Alert severity="success">
            {t('global_setting.postprocessing_retry_empty', 'No post-processing retries are currently waiting.')}
          </Alert>
        ) : (
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('global_setting.postprocessing_retry_event', 'TimeEvent')}</TableCell>
                  <TableCell>{t('global_setting.postprocessing_retry_action', 'Action')}</TableCell>
                  <TableCell>{t('global_setting.postprocessing_retry_attempt', 'Attempt')}</TableCell>
                  <TableCell>{t('global_setting.postprocessing_retry_next_attempt', 'Next attempt')}</TableCell>
                  <TableCell>{t('global_setting.postprocessing_retry_error_label', 'Last error')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {postprocessingRetries.map((retry) => (
                  <TableRow key={retry.id} hover>
                    <TableCell sx={{ minWidth: 230, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {retry.event_source || t('global_setting.postprocessing_retry_event_id', 'TimeEvent #{{id}}', {
                          id: retry.timeevent_id
                        })}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {retry.project_name || t('global_setting.postprocessing_retry_project_unknown', 'Unknown project')}
                        {' · '}
                        {t('global_setting.postprocessing_retry_event_id_short', '#{{id}}', {
                          id: retry.timeevent_id
                        })}
                      </Typography>
                      {retry.event_time && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatTimestamp(retry.event_time)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 190, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {retry.action_name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {retry.flow_name || t('global_setting.postprocessing_retry_flow_unknown', 'No flow')}
                        {retry.action_type ? ` · ${retry.action_type}` : ''}
                      </Typography>
                      {retry.phase && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {t('global_setting.postprocessing_retry_phase', 'Phase: {{phase}}', {
                            phase: retry.phase
                          })}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, verticalAlign: 'top' }}>
                      <Chip
                        size="small"
                        color={getRetryStateColor(retry.state, retry.is_stale)}
                        label={getRetryStateLabel(retry.state, retry.is_stale)}
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {retry.attempt}/{retry.max_attempts}
                        {' · '}
                        {t('global_setting.postprocessing_retry_generation', 'Generation {{generation}}', {
                          generation: retry.generation
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, verticalAlign: 'top' }}>
                      {formatTimestamp(retry.next_attempt_at)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 260, maxWidth: 440, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {retry.last_error || t('global_setting.postprocessing_retry_no_error', 'No error message recorded.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Search */}
          <TextField
            label="Search logs"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Log file selector */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Log File</InputLabel>
            <Select
              value={selectedLogFile}
              label="Log File"
              onChange={(e) => {
                setSelectedLogFile(e.target.value);
                // Auto-refresh when changing log file
                setTimeout(() => {
                  setLoading(true);
                  sendCommand('get_logs', { limit: 1000, log_file: e.target.value });
                }, 100);
              }}
            >
              <MenuItem value="all">All Services (Aggregated)</MenuItem>
              {availableLogs.map((logFile) => (
                <MenuItem key={logFile} value={logFile}>
                  {logFile.replace('_output.log', '').replace('_error.log', ' (errors)').replace(/_/g, ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Level filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select
              value={levelFilter}
              label="Level"
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Tooltip title="Refresh logs">
              <IconButton 
                onClick={fetchLogs}
                disabled={loading}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>            <Tooltip title="Download logs">
              <IconButton 
                onClick={downloadLogs}
                disabled={logs.length === 0}
                color="primary"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${Array.isArray(logs) ? logs.length : 0}`} 
            size="small" 
            color="default"
          />
          <Chip 
            label={`Showing: ${Array.isArray(filteredLogs) ? filteredLogs.length : 0}`} 
            size="small" 
            color="primary"
          />
          <Chip 
            label={`Source: ${selectedLogFile === 'all' ? 'All Services' : selectedLogFile}`} 
            size="small" 
            color="secondary"
            variant="outlined"
          />
          {lastRefresh && (
            <Chip 
              label={`Last refresh: ${formatTimestamp(lastRefresh)}`} 
              size="small" 
              color="default"
              variant="outlined"
            />
          )}
        </Box>{/* Auto-refresh toggle - DISABLED */}
        {/* <Box sx={{ mt: 2 }}>
          <Button
            variant={autoRefresh ? "contained" : "outlined"}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
            color="primary"
          >
            {autoRefresh ? 'Stop Auto-refresh' : 'Start Auto-refresh'}
          </Button>
        </Box> */}
      </Paper>      {/* Logs display */}
      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading logs...
        </Alert>
      )}      {(!Array.isArray(filteredLogs) || filteredLogs.length === 0) && !loading ? (
        <Alert severity="info">
          No logs found. {debouncedSearchTerm || levelFilter !== 'all' ? 'Try adjusting your filters.' : 'System logs will appear here.'}
        </Alert>
      ) : (        <Paper ref={listContainerRef} sx={{ height: 600, overflow: 'hidden' }}>
          <VirtualList
            ref={listRef}
            height={600}
            width="100%"
            itemCount={filteredLogs.length}
            itemSize={getItemSize}
            itemData={virtualListData}
            overscanCount={5}
          >
            {LogItem}
          </VirtualList>
        </Paper>
      )}
    </Box>
  );
});

GlobalSettingLogs.displayName = 'GlobalSettingLogs';

export default GlobalSettingLogs;
